import { isRecord } from "@/lib/is-record";
import { buildHref } from "@softmax/web-addresses";
import type { V2EpisodeDetail } from "@softmax/observatory-client/types";
import type { AddressPick } from "@/lib/leagues/replay-types";

// Platform-filled seats do not identify the competing players.
function fillerSeats(detail: V2EpisodeDetail): Set<number> {
  const raw = detail.tags.filler_seats;

  if (typeof raw !== "string" || raw.trim() === "") return new Set();

  const seats = raw
    .split(",")
    .map((part) => Number.parseInt(part.trim(), 10))
    .filter((seat) => Number.isInteger(seat) && seat >= 0);
  return new Set(seats);
}

function resultsOf(detail: V2EpisodeDetail): Record<string, unknown> | null {
  const coworld = detail.attributes.coworld;

  if (!isRecord(coworld)) return null;

  return isRecord(coworld.results) ? coworld.results : null;
}

// Repeated seat labels identify one player, even when they occupy several seats.
function baseName(name: string): string {
  return name.replace(/\s*\(\d+\)$/, "");
}

function distinct(names: string[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];

  for (const name of names) {
    const base = baseName(name);

    if (seen.has(base)) continue;

    seen.add(base);
    out.push(base);
  }

  return out;
}

function stringList(value: unknown): string[] | null {
  if (!Array.isArray(value)) return null;

  if (!value.every((entry) => typeof entry === "string")) return null;

  return value;
}

// Seat order avoids revealing the winning side before the replay begins.
function episodeSides(detail: V2EpisodeDetail): string[][] {
  const results = resultsOf(detail);

  if (!results) return [];

  const names = stringList(results.names);

  if (!names || names.length === 0) return [];

  const filler = fillerSeats(detail);
  const seats = names
    .map((name, seat) => ({ name, seat }))
    .filter((entry) => !filler.has(entry.seat));

  if (seats.length === 0) return [];

  const teams = stringList(results.teams);

  if (teams && teams.length === names.length) {
    const order: string[] = [];

    for (const entry of seats) {
      const team = teams[entry.seat];

      if (team != null && !order.includes(team)) order.push(team);
    }

    if (order.length > 0) {
      return order.map((team) =>
        distinct(
          seats
            .filter((entry) => teams[entry.seat] === team)
            .map((entry) => entry.name),
        ),
      );
    }
  }

  return distinct(seats.map((entry) => entry.name)).map((name) => [name]);
}

export function replayFromEpisodeDetail(
  detail: V2EpisodeDetail,
): AddressPick | null {
  const replayUrl = detail.replay_url;
  const coworldId = detail.coworld_id ?? detail.tags.coworld_id ?? null;

  if (!replayUrl || !coworldId) return null;

  const sides = episodeSides(detail);
  const [firstSide, ...otherSides] = sides;
  const field: [string, ...string[]] = firstSide
    ? [firstSide.join(", "), ...otherSides.map((side) => side.join(", "))]
    : [detail.coworld_name ?? "Episode"];
  return {
    episodeId: detail.id,
    coworldId,
    coworldName: detail.coworld_name ?? null,
    coworldVersion: detail.coworld_version ?? null,
    replayUrl,
    // Shared links play the recording, including when a live spectator URL exists.
    liveUrl: null,
    live: false,
    finishedAt: detail.created_at,
    roundNumber: 0,
    episodeNumber: null,
    code: detail.id.slice(0, 8),
    field,
    label: detail.coworld_name ?? "episode",
    inspectUrl: buildHref({
      name: "episode",
      episodeId: detail.id,
      game: detail.game_address,
    }),
    reason: "shared link",
    unranked: true,
  };
}
